import { Observable } from 'rxjs';
import {
  FilePreviewSource,
  canPreviewFile,
  fileNameFromPath,
  getFileExtension,
} from '../../components/shared/file-preview-modal/file-preview.types';
import { TicketDocument } from '../models/ticket.model';

// Ponte entre `ticket_documents` e o pré-visualizador genérico. Fica separado do contrato do
// viewer para que ele não passe a conhecer chamados, e é a única cópia dessa regra — usada
// tanto pela lista de anexos quanto pela página de preview em tela cheia.

/**
 * Anexo do helpdesk legado (Teorema EE). `link_file` é um caminho UNC da rede interna, que o
 * browser não consegue abrir; os bytes só chegam pelo proxy reverso, a partir do `ref`.
 */
export function isLegacyAttachment(doc: TicketDocument): boolean {
  return !!doc.link_file?.startsWith('\\');
}

/**
 * Nome com extensão. `link_file` é a fonte primária (o backend preserva a extensão ao subir
 * para o Spaces, e o caminho UNC do legado também termina no arquivo); `observation` entra só
 * como reserva, porque para os uploads novos ele guarda o nome original do arquivo.
 */
export function attachmentFileName(doc: TicketDocument): string {
  const fromLink = fileNameFromPath(doc.link_file);
  if (getFileExtension(fromLink)) return fromLink;

  const fromObservation = fileNameFromPath(doc.observation);
  if (getFileExtension(fromObservation)) return fromObservation;

  return fromLink || fromObservation || 'Anexo';
}

/**
 * Se dá para pré-visualizar: extensão suportada **e** origem alcançável. Um anexo legado sem
 * `ref` não tem de onde buscar os bytes, e um `link_file` que não é http nem UNC não é algo que
 * saibamos abrir — nos dois casos o botão de visualizar simplesmente não aparece, e o anexo
 * continua se comportando como sempre.
 */
export function canPreviewTicketAttachment(doc: TicketDocument): boolean {
  if (!canPreviewFile(attachmentFileName(doc))) return false;
  if (isLegacyAttachment(doc)) return !!doc.ref;
  return !!doc.link_file?.startsWith('http');
}

/**
 * Monta a fonte do viewer. A diferença entre os dois mundos fica toda aqui, e é transparente
 * para o usuário: anexo do Spaces vira URL direta (o PDF nem passa por uma request nossa),
 * anexo legado vira um carregador por blob via proxy.
 *
 * `loadBlob` é recebido pronto para não acoplar este util ao `TicketService`.
 */
export function buildTicketAttachmentPreviewSource(
  doc: TicketDocument,
  loadBlob: () => Observable<Blob>
): FilePreviewSource {
  const legacy = isLegacyAttachment(doc);
  const fileName = attachmentFileName(doc);

  return {
    fileName,
    title: doc.observation?.trim() || fileName,
    url: legacy ? null : (doc.link_file ?? null),
    loadBlob: legacy ? loadBlob : undefined,
    // Legado só tem link compartilhável quando há `ref` (a URL do proxy é montada a partir dele).
    allowExternalLink: !legacy || !!doc.ref,
  };
}
